import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Save, X, MapPin } from 'lucide-react';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useToast } from '../../context/ToastContext';
import apiClient from '../../utils/api';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';

// Fix default marker icons broken by webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const EMPTY_BRANCH = { name: '', code: '', email: '', phone: '', address: '', city: '', latitude: '', longitude: '' };
const DEFAULT_CENTER: [number, number] = [-1.9441, 30.0619]; // Kigali, Rwanda

function ClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

function FlyTo({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => { map.flyTo([lat, lng], 15); }, [lat, lng, map]);
  return null;
}

export const CompanyAdminBranches: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [branches, setBranches] = useState<any[]>([]);
  const [shipmentCounts, setShipmentCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editBranch, setEditBranch] = useState<any>(null);
  const [form, setForm] = useState({ ...EMPTY_BRANCH });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [statusTarget, setStatusTarget] = useState<{ branch: any; next: string } | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [flyTo, setFlyTo] = useState<{ lat: number; lng: number } | null>(null);

  const load = useCallback(async () => {
    if (!user?.companyId) return;
    setLoading(true);
    try {
      const [branchRes, shipRes] = await Promise.all([
        apiClient.listBranches(user.companyId, 100, 0),
        apiClient.listShipments({}, 1000, 0),
      ]);
      const bd = branchRes.data.data;
      const branchList: any[] = bd?.branches ?? bd?.rows ?? (Array.isArray(bd) ? bd : []);
      setBranches(branchList);
      const sd = shipRes.data.data;
      const shipList: any[] = sd?.shipments ?? sd?.rows ?? (Array.isArray(sd) ? sd : []);
      const counts: Record<string, number> = {};
      branchList.forEach(b => { counts[b.id] = shipList.filter(s => s.departmentBranchId === b.id).length; });
      setShipmentCounts(counts);
    } catch {} finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditBranch(null); setForm({ ...EMPTY_BRANCH }); setShowForm(true); setShowMap(false); setFlyTo(null); setError(''); };
  const openEdit = (b: any) => {
    setEditBranch(b);
    setForm({ name: b.name, code: b.code, email: b.email ?? '', phone: b.phone ?? '', address: b.address ?? '', city: b.city ?? '', latitude: b.latitude ?? '', longitude: b.longitude ?? '' });
    setShowForm(true);
    setShowMap(false);
    setFlyTo(b.latitude && b.longitude ? { lat: parseFloat(b.latitude), lng: parseFloat(b.longitude) } : null);
    setError('');
  };

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleMapPick = (lat: number, lng: number) => {
    const latStr = lat.toFixed(6);
    const lngStr = lng.toFixed(6);
    setForm(f => ({ ...f, latitude: latStr, longitude: lngStr }));
    setFlyTo({ lat, lng });
  };

  const handleLatLngInput = (k: 'latitude' | 'longitude', v: string) => {
    set(k, v);
    const lat = k === 'latitude' ? parseFloat(v) : parseFloat(form.latitude);
    const lng = k === 'longitude' ? parseFloat(v) : parseFloat(form.longitude);
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      setFlyTo({ lat, lng });
    }
  };

  const markerPos: [number, number] | null =
    form.latitude && form.longitude && !isNaN(parseFloat(form.latitude)) && !isNaN(parseFloat(form.longitude))
      ? [parseFloat(form.latitude), parseFloat(form.longitude)]
      : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload: any = { ...form };
      if (payload.latitude) payload.latitude = parseFloat(payload.latitude);
      if (payload.longitude) payload.longitude = parseFloat(payload.longitude);
      if (editBranch) await apiClient.updateBranch(user!.companyId!, editBranch.id, payload);
      else await apiClient.createBranch(user!.companyId!, payload);
      toast(editBranch ? 'Branch updated' : 'Branch created', 'success');
      setShowForm(false); load();
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to save branch');
    } finally { setSaving(false); }
  };

  const confirmToggleStatus = async () => {
    if (!statusTarget) return;
    try {
      await apiClient.updateBranchStatus(user!.companyId!, statusTarget.branch.id, statusTarget.next);
      toast(`Branch ${statusTarget.next === 'ACTIVE' ? 'activated' : 'deactivated'}`, 'success');
      load();
    } catch (e: any) { toast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setStatusTarget(null); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.deleteBranch(user!.companyId!, deleteTarget.id);
      toast('Branch deleted', 'success');
      load();
    } catch (e: any) { toast(e.response?.data?.error || 'Failed to delete branch', 'error'); }
    finally { setDeleteTarget(null); }
  };

  const mapCenter: [number, number] = markerPos ?? DEFAULT_CENTER;

  return (
    <>
      {statusTarget && (
        <ConfirmModal
          title={statusTarget.next === 'ACTIVE' ? 'Activate Branch' : 'Deactivate Branch'}
          message={`${statusTarget.next === 'ACTIVE' ? 'Activate' : 'Deactivate'} branch "${statusTarget.branch.name}"?`}
          confirmLabel={statusTarget.next === 'ACTIVE' ? 'Activate' : 'Deactivate'}
          danger={statusTarget.next === 'INACTIVE'}
          onConfirm={confirmToggleStatus}
          onCancel={() => setStatusTarget(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          title="Delete Branch"
          message={`Permanently delete branch "${deleteTarget.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="page-header">
        <h1 className="page-title">Branches</h1>
        <button className="btn btn-primary" onClick={openNew}><Plus size={16} /> New Branch</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20, maxWidth: 760 }}>
          <div className="page-header" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>{editBranch ? 'Edit Branch' : 'New Branch'}</h2>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}><X size={16} /></button>
          </div>
          {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{error}</div>}
          <form onSubmit={submit}>
            <div className="form-grid">
              <div className="form-group"><label>Branch Name *</label><input required value={form.name} onChange={e => set('name', e.target.value)} /></div>
              <div className="form-group"><label>Branch Code *</label><input required value={form.code} onChange={e => set('code', e.target.value)} /></div>
              <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
              <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
              <div className="form-group full"><label>Address</label><input value={form.address} onChange={e => set('address', e.target.value)} /></div>
              <div className="form-group"><label>City</label><input value={form.city} onChange={e => set('city', e.target.value)} /></div>

              {/* Lat/Lng with map toggle */}
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Latitude</label>
                <input
                  type="number" step="any" value={form.latitude}
                  onChange={e => handleLatLngInput('latitude', e.target.value)}
                  placeholder="e.g. -1.9441"
                />
              </div>
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Longitude</label>
                <input
                  type="number" step="any" value={form.longitude}
                  onChange={e => handleLatLngInput('longitude', e.target.value)}
                  placeholder="e.g. 30.0619"
                />
              </div>

              <div className="form-group full">
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                  onClick={() => setShowMap(v => !v)}
                >
                  <MapPin size={14} />
                  {showMap ? 'Hide Map' : (markerPos ? 'View / Change on Map' : 'Pick Location on Map')}
                </button>
                {markerPos && !showMap && (
                  <span style={{ marginLeft: 12, fontSize: 12, color: '#6b7280' }}>
                    📍 {parseFloat(form.latitude).toFixed(5)}, {parseFloat(form.longitude).toFixed(5)}
                  </span>
                )}
              </div>

              {showMap && (
                <div className="form-group full">
                  <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Click on the map to set the branch location.</p>
                  <div style={{ height: 320, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                    <MapContainer
                      center={mapCenter}
                      zoom={markerPos ? 15 : 12}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      />
                      <ClickHandler onPick={handleMapPick} />
                      {flyTo && <FlyTo lat={flyTo.lat} lng={flyTo.lng} />}
                      {markerPos && <Marker position={markerPos} />}
                    </MapContainer>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}><Save size={15} /> {saving ? 'Saving...' : 'Save Branch'}</button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {loading ? <div className="loading-spinner">Loading branches...</div> : branches.length === 0 ? (
          <div className="empty-state"><h3>No branches yet</h3><p>Create your first branch to get started.</p></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Name</th><th>Code</th><th>City</th><th>Email</th><th>Phone</th><th>Location</th><th>Shipments</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {branches.map((b: any) => (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 500 }}>{b.name}</td>
                    <td><code style={{ fontSize: 12 }}>{b.code}</code></td>
                    <td>{b.city || '—'}</td>
                    <td>{b.email || '—'}</td>
                    <td>{b.phone || '—'}</td>
                    <td>
                      {b.latitude && b.longitude ? (
                        <a
                          href={`https://www.openstreetmap.org/?mlat=${b.latitude}&mlon=${b.longitude}&zoom=15`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 12, color: '#3b82f6', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                        >
                          <MapPin size={12} /> Map
                        </a>
                      ) : '—'}
                    </td>
                    <td style={{ fontWeight: 600, color: '#3b82f6' }}>{shipmentCounts[b.id] ?? 0}</td>
                    <td><span className={`badge badge-${b.status?.toLowerCase()}`}>{b.status}</span></td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => openEdit(b)}>Edit</button>
                      <button
                        className={`btn ${b.status === 'ACTIVE' ? 'btn-danger' : 'btn-success'}`}
                        style={{ padding: '3px 8px', fontSize: 12 }}
                        onClick={() => setStatusTarget({ branch: b, next: b.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' })}
                      >
                        {b.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '3px 8px', fontSize: 12, color: '#ef4444' }}
                        onClick={() => setDeleteTarget(b)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};
